import { Autocomplete, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import StatusChip from "Pages/controlPlane/components/StatusChip";

import { getMaintenanceScheduleAction, updateMaintenanceScheduleAction } from "Actions/maintenanceSchedule";
import { createMaintenanceScheduleAction } from "Actions/maintenanceSchedule";
import DateTimeComponent from "./components/DateTimePicker";

const maintenanceTypes = ["patch", "minor", "major"];

const TYPE_COLORS = {
  patch: { bg: "#D5F5E3", text: "#0B6E4F" },
  minor: { bg: "#EBF5FB", text: "#154360" },
  major: { bg: "#FADBD8", text: "#922B21" },
};

function MaintenanceScheduleList(props) {
  const [selectedItem, setSelectedItem] = useState(null); // for edit side panel
  const [isCreating, setIsCreating] = useState(false); // for create side panel
  const [updateData, setUpdateData] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    props.getMaintenanceScheduleAction();
  }, [props.customerListReset, props.updateDataReset]);

  const handleMaintenanceName = (event) => {
    setUpdateData({ ...updateData, name: event.target.value });
  };

  const handleMaintenanceType = (event, value) => {
    if (value != updateData.type) {
      setUpdateData({ ...updateData, type: value });
    }
  };

  const handleDescription = (event) => {
    setUpdateData({ ...updateData, description: event.target.value });
  };

  const handleStartDate = (value) => {
    setUpdateData({ ...updateData, from: value });
  };

  const handleEndDate = (value) => {
    setUpdateData({ ...updateData, till: value });
  };

  const handleEdit = (item) => {
    setIsCreating(false);
    setSelectedItem(item._id);
    setUpdateData(item);
    setErrorMessage("");
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsCreating(true);
    setUpdateData({});
    setErrorMessage("");
  };

  const closePanel = () => {
    setSelectedItem(null);
    setIsCreating(false);
    setUpdateData({});
    setErrorMessage("");
  };

  const handleSubmit = (context) => {
    if (updateData.name == "" || updateData.name == undefined) {
      setErrorMessage("mandatory field *");
      return;
    } else if (updateData.from === "" | updateData.from == undefined) {
      setErrorMessage("mandatory field *");
      return;
    } else if (updateData.till === "" | updateData.till == undefined) {
      setErrorMessage("mandatory field *");
      return;
    } else if (updateData.type === "" | updateData.type == undefined) {
      setErrorMessage("mandatory field *");
      return;
    }

    if (context === "create") {
      props.createMaintenanceScheduleAction({
        body: {
          type: updateData.type,
          from: updateData.from,
          till: updateData.till,
          description: updateData.description,
          name: updateData.name,
        },
      });
    } else {
      props.updateMaintenanceScheduleAction({
        body: {
          _id: updateData._id,
          type: updateData.type,
          from: updateData.from,
          till: updateData.till,
          description: updateData.description,
          name: updateData.name,
        },
        params: { id: updateData?._id },
      });
    }
    closePanel();
  };

  const getScheduledTimePeriod = (from, till) => {
    const startDate = new Date(from);
    const endDate = new Date(till);
    const currentDate = new Date();

    const startDay = startDate.getDate();
    const starttime = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "numeric", hour12: true }).format(startDate);
    const endtime = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "numeric", hour12: true }).format(endDate);

    const endDay = endDate.getDate();
    const monthName = new Intl.DateTimeFormat("en-US", { month: "short" }).format(startDate);
    const year = startDate.getUTCFullYear();

    let scheduleString;
    if (startDay === endDay) {
      scheduleString = `${monthName} ${startDay}, ${year} From ${starttime} to ${endtime}`;
    } else {
      scheduleString = `${monthName} ${startDay}, ${year} ${starttime} to ${endDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "numeric", hour12: true })}`;
    }

    let status;
    if (currentDate > endDate) {
      status = "expired";
    } else if (currentDate < startDate) {
      status = "scheduled";
    } else {
      status = "live";
    }

    return { schedule: scheduleString, status };
  };

  // Process data
  let rowData = (props?.maintenanceData?.maintenanceSchedules || [])
    .map((x) => {
      const { schedule, status } = getScheduledTimePeriod(x.from, x.till);
      return { ...x, schedule, status };
    })
    .sort((a, b) => {
      const order = { live: 0, scheduled: 1, expired: 2 };
      return (order[a.status] ?? 3) - (order[b.status] ?? 3);
    });

  // Filter
  const STATUS_FILTERS = ["All", "Live", "Scheduled", "Expired"];
  const FILTER_COLORS = {
    All: { bg: "#E0F7FA", text: "#00ACC4", border: "#00ACC4" },
    Live: { bg: "#D5F5E3", text: "#0B6E4F", border: "#17A589" },
    Scheduled: { bg: "#F4ECF7", text: "#512E5F", border: "#8E44AD" },
    Expired: { bg: "#F2F3F4", text: "#5D6D7E", border: "#95A5A6" },
  };
  const filtered = rowData.filter((t) => {
    return filterStatus === "All" || t.status.toLowerCase() === filterStatus.toLowerCase();
  });

  const showPanel = selectedItem || isCreating;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3} sx={{ display: "flex", gap: 3, overflow: "hidden" }}>
        {/* Main Content */}
        <MDBox flex={1} minWidth={0}>
          {/* Page Header */}
          <MDBox mb={2.5}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <span style={{ color: "#5D6D7E", fontSize: 12 }}>Control Plane</span>
              <span style={{ color: "#BDC3C7" }}>›</span>
              <span style={{ color: "#1E3A5F", fontSize: 12, fontWeight: 600 }}>Maintenance</span>
            </MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="space-between">
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1E3A5F" }}>Maintenance Schedules</h1>
              <button
                onClick={handleAdd}
                style={{
                  background: "linear-gradient(135deg, #00ACC4 0%, #2F36CB 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 18px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                + Schedule Maintenance
              </button>
            </MDBox>
          </MDBox>

          {/* Filters */}
          <MDBox display="flex" gap={1} mb={2} alignItems="center" flexWrap="wrap">
            <span style={{ fontSize: 12, color: "#5D6D7E", fontWeight: 500 }}>Status:</span>
            {STATUS_FILTERS.map((s) => {
              const isActive = filterStatus === s;
              const colors = FILTER_COLORS[s] || FILTER_COLORS.All;
              return (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 20,
                    border: `1px solid ${isActive ? colors.border : "#E8EDF2"}`,
                    background: isActive ? colors.bg : "#fff",
                    color: isActive ? colors.text : "#5D6D7E",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {s}
                </button>
              );
            })}
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#5D6D7E" }}>
              {filtered.length} schedule{filtered.length !== 1 ? "s" : ""}
            </span>
          </MDBox>

          {/* Table */}
          <div style={{ background: "#fff", border: "1px solid #E8EDF2", borderRadius: 10, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "17%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "21%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E8EDF2" }}>
                  {["Name", "Type", "Schedule", "Status", "Description", ""].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        color: "#5D6D7E",
                        fontWeight: 600,
                        fontSize: 13,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "#5D6D7E" }}>
                      No maintenance schedules found.
                    </td>
                  </tr>
                )}
                {filtered.map((t, i) => {
                  const typeColor = TYPE_COLORS[t.type] || { bg: "#F2F3F4", text: "#5D6D7E" };
                  const chipStatus = t.status === "live" ? "Active" : t.status === "scheduled" ? "Provisioning" : "Archived";

                  return (
                    <tr
                      key={t._id || i}
                      onClick={() => handleEdit(t)}
                      style={{
                        borderBottom: "1px solid #F0F3F6",
                        cursor: "pointer",
                        background: selectedItem === t._id ? "#D4E6F1" : i % 2 === 0 ? "#fff" : "#FAFBFC",
                        transition: "background 0.1s",
                      }}
                    >
                      <td style={{ padding: "11px 14px" }}>
                        <div style={{ fontWeight: 600, color: "#1E3A5F" }}>
                          {(t.name || "").charAt(0).toUpperCase() + (t.name || "").slice(1)}
                        </div>
                      </td>
                      <td style={{ padding: "11px 14px" }}>
                        <span
                          style={{
                            background: typeColor.bg,
                            color: typeColor.text,
                            padding: "3px 10px",
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {(t.type || "").charAt(0).toUpperCase() + (t.type || "").slice(1)}
                        </span>
                      </td>
                      <td style={{ padding: "11px 14px", color: "#5D6D7E", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.schedule}
                      </td>
                      <td style={{ padding: "11px 14px" }}>
                        <StatusChip status={chipStatus} />
                      </td>
                      <td style={{ padding: "11px 14px", color: "#5D6D7E", fontSize: 12, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.description || "—"}
                      </td>
                      <td style={{ padding: "11px 14px" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(t); }}
                          style={{
                            padding: "6px 16px",
                            borderRadius: 6,
                            border: "1px solid #E8EDF2",
                            background: "#fff",
                            fontSize: 13,
                            cursor: "pointer",
                            color: "#00ACC4",
                            fontWeight: 600,
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ padding: "10px 16px", borderTop: "1px solid #F0F3F6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "#5D6D7E" }}>
                Showing {filtered.length} of {rowData.length} schedules
              </span>
            </div>
          </div>
        </MDBox>

        {/* Right Side Panel — Edit / Create */}
        {showPanel && (
          <MDBox
            sx={{
              width: 360,
              flexShrink: 0,
              background: "#fff",
              border: "1px solid #E8EDF2",
              borderRadius: "12px",
              overflow: "auto",
              height: "fit-content",
              maxHeight: "calc(100vh - 120px)",
              animation: "slideIn 0.2s ease",
              "@keyframes slideIn": {
                from: { opacity: 0, transform: "translateX(20px)" },
                to: { opacity: 1, transform: "translateX(0)" },
              },
            }}
          >
            {/* Panel Header */}
            <MDBox sx={{ background: "#1E3A5F", p: 2.5, position: "sticky", top: 0, zIndex: 1 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDBox>
                  <MDTypography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                    {isCreating ? "Schedule Maintenance" : "Update Maintenance"}
                  </MDTypography>
                  <MDTypography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, mt: 0.3 }}>
                    {isCreating ? "Create a new maintenance window" : updateData.name || ""}
                  </MDTypography>
                </MDBox>
                <button
                  onClick={closePanel}
                  style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", cursor: "pointer", borderRadius: 6, padding: "4px 8px", fontSize: 14 }}
                >
                  ✕
                </button>
              </MDBox>
              {!isCreating && updateData.status && (
                <MDBox display="flex" gap={1} mt={1.5}>
                  <StatusChip status={updateData.status === "live" ? "Active" : updateData.status === "scheduled" ? "Provisioning" : "Archived"} />
                  {updateData.type && (
                    <span style={{
                      background: (TYPE_COLORS[updateData.type] || {}).bg || "#F2F3F4",
                      color: (TYPE_COLORS[updateData.type] || {}).text || "#5D6D7E",
                      padding: "3px 10px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                    }}>
                      {(updateData.type || "").charAt(0).toUpperCase() + (updateData.type || "").slice(1)}
                    </span>
                  )}
                </MDBox>
              )}
            </MDBox>

            {/* Panel Body */}
            <MDBox p={2.5}>
              {/* Form Fields */}
              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>
                  Maintenance Name *
                </MDTypography>
                <MDInput
                  onChange={handleMaintenanceName}
                  value={updateData.name || ""}
                  fullWidth
                  size="small"
                  placeholder="e.g. Monthly Security Patch"
                  error={errorMessage && (!updateData.name)}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>
                  Type *
                </MDTypography>
                <Autocomplete
                  disablePortal
                  size="small"
                  options={maintenanceTypes}
                  renderInput={(params) => <TextField {...params} placeholder="Select type" />}
                  onChange={handleMaintenanceType}
                  fullWidth
                  value={updateData.type || null}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>
                  Start Date & Time *
                </MDTypography>
                <DateTimeComponent
                  selectedDate={updateData.from && updateData.from.slice(0, 16)}
                  setSelectedDate={handleStartDate}
                  label={"Start"}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>
                  End Date & Time *
                </MDTypography>
                <DateTimeComponent
                  selectedDate={updateData.till && updateData.till.slice(0, 16)}
                  setSelectedDate={handleEndDate}
                  label={"End"}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "#1E3A5F", mb: 0.5 }}>
                  Description
                </MDTypography>
                <MDInput
                  onChange={handleDescription}
                  value={updateData.description || ""}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  placeholder="Optional description..."
                />
              </MDBox>

              {errorMessage && (
                <MDTypography sx={{ fontSize: 12, color: "#E74C3C", mb: 2 }}>
                  {errorMessage}
                </MDTypography>
              )}

              {/* Actions */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <button
                  onClick={() => handleSubmit(isCreating ? "create" : "update")}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    border: "none",
                    background: "#00ACC4",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {isCreating ? "Create" : "Update"}
                </button>
                <button
                  onClick={closePanel}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    border: "none",
                    background: "#F2F3F4",
                    color: "#5D6D7E",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>

              {/* Warning */}
              <MDTypography sx={{ fontSize: 12, color: "#C0392B", mt: 2, fontStyle: "bold" }}>
                * Creating/Updating will trigger a notification email to all Vivre users.
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

const mapStateToProps = (state) => {
  return {
    customersData: state.getCustomersDataReducer.customersData,
    customerListReset: state.disableCustomerReducer.customerListReset,
    updateDataReset: state.updateCustomerReducer.updateDataReset,
    maintenanceData: state.maintenanceSchedulesReducer?.maintenanceScheduleData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getMaintenanceScheduleAction: () => {
      dispatch(getMaintenanceScheduleAction());
    },
    updateMaintenanceScheduleAction: (data) => {
      dispatch(updateMaintenanceScheduleAction(data));
    },
    createMaintenanceScheduleAction: (data) => {
      dispatch(createMaintenanceScheduleAction(data));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceScheduleList);
